'use client';

import React, { type Dispatch, type SetStateAction } from 'react';

import { useTranslations } from 'next-intl';

import { AppleButton } from '@/components/AppleButton';
import { decodeJWT } from '@/modules/auth/decodeJWT';
import {
  type AppleSignInResponse,
  SSOButtonVariant,
} from '@/modules/auth/types';
import { fetchAppleCredentials } from '@/modules/login/api';
import {
  handleSSOError,
  setAccountLinkingCookies,
} from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useLoginTracking } from '@/modules/login/useLoginTracking';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { useSignupTracking } from '@/modules/signup/hooks/useSignupTracking';
import { SignupType } from '@/modules/signup/types';

type Props = {
  setError: Dispatch<SetStateAction<string>>;
  setIsAccountLinkingError: Dispatch<SetStateAction<boolean>>;
};

function AppleLoginButton({ setError, setIsAccountLinkingError }: Props) {
  const { appleLoginMutation } = useLoginMutations({
    loginType: LoginType.Apple,
  });
  const { signupWithSSO } = useSSOSignup({
    ssoType: SignupType.Apple,
  });
  const t = useTranslations('login');
  const { sendSignupSubmitActionEvent } = useSignupTracking();
  const { sendLoginSubmitAction } = useLoginTracking();

  async function handleAppleIDResponse(
    credentials: AppleSignInResponse,
    redirect_uri: string
  ) {
    let apple_user_id: string;
    let apple_access_token: string;

    try {
      ({
        data: { apple_user_id, apple_access_token },
      } = await fetchAppleCredentials({
        code: credentials.authorization.code,
        redirect_uri,
      }));
    } catch {
      return setError('Error.SocialProviderLogin');
    }

    const { email, name } = credentials.user
      ? credentials.user
      : decodeJWT(credentials.authorization.id_token);

    function handleUserNotFound() {
      sendSignupSubmitActionEvent(SignupType.Apple);

      return signupWithSSO({
        email,
        credentials: { apple_user_id, apple_access_token },
        name,
      });
    }

    function handleAccountNotLinked() {
      setAccountLinkingCookies({ apple_user_id, apple_access_token }, email);

      setIsAccountLinkingError(true);

      return setError(
        t('SSO.AccountExistsSpecific', {
          socialProvider: 'Apple',
        })
      );
    }

    appleLoginMutation.mutate(
      {
        apple_access_token,
        apple_user_id,
      },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError,
            onUserNotFound: handleUserNotFound,
            onAccountNotLinked: handleAccountNotLinked,
          });
        },
      }
    );
  }

  return (
    <AppleButton
      variant={SSOButtonVariant.EyeCatching}
      handleSubmitTracking={() => sendLoginSubmitAction(LoginType.Apple)}
      handleAppleIDResponse={handleAppleIDResponse}
    />
  );
}
export { AppleLoginButton };
